import type { Capability, CurrentUser } from '@/types/domain'

/* Capability tests for the interface.
 *
 * Roles became runtime-definable with migration 0021, which superseded AD-7's
 * fixed five. That changed what the client is allowed to ask. It used to ask
 * "is this user Finance" and render accordingly, because the five roles were
 * knowable at build time; a founder can now create a role this build has never
 * heard of, so that question has no useful answer — a custom role matches none
 * of the five arms and a `switch` on it silently falls through to whichever
 * branch happens to be last.
 *
 * So the interface asks "may this user issue an invoice" instead, and the
 * server answers by listing the capabilities it resolved on this very request.
 *
 * NONE OF THIS IS AUTHORIZATION. Every capability tested here is re-checked
 * server-side on the request that acts on it (AD-21, FR-56). Hiding a button
 * the server would refuse is a courtesy to the user, not a control — a client
 * that granted itself a capability would get a 403 for its trouble. The reason
 * to be careful here is that a WRONGLY HIDDEN button looks like a broken
 * product, not that a wrongly shown one is a breach.
 */

/** Whether a user holds a capability.
 *
 * A null or undefined user answers false, which is what the loading state and
 * the signed-out state should both render as: an interface that shows every
 * button for the instant before the session resolves is worse than one that
 * shows none and fills in.
 *
 * The superuser short-circuits, so a founder's interface is complete even for
 * capabilities added after this build shipped. */
export function can(user: CurrentUser | null | undefined, capability: Capability): boolean {
  if (!user) return false
  if (user.isSuperuser) return true
  return user.capabilities.includes(capability)
}

/** Whether a user holds EVERY one of the given capabilities.
 *
 * For actions gated on two at once, such as an invoice that must be drafted
 * before it can be issued. */
export function canAll(
  user: CurrentUser | null | undefined,
  ...capabilities: Capability[]
): boolean {
  return capabilities.every((c) => can(user, c))
}

/** Whether a user holds ANY of the given capabilities.
 *
 * For a section that several distinct permissions can open — a navigation entry
 * that leads to a screen with more than one thing on it. */
export function canAny(
  user: CurrentUser | null | undefined,
  ...capabilities: Capability[]
): boolean {
  return capabilities.some((c) => can(user, c))
}

/* The capability keys this client tests, spelled once.
 *
 * Duplicated from the server's registry in platform/authz, the same way the
 * role strings were duplicated before it — the client cannot import Go, and a
 * key spelled inline at a call site is a key that gets misspelled. A typo here
 * fails CLOSED: `can()` finds no such capability in the list and hides the
 * button, which is visible immediately rather than silently permissive.
 *
 * Only the keys the interface actually branches on are listed. The full
 * vocabulary is served by GET /api/admin/capabilities for the role editor,
 * which renders whatever the server registers rather than this list. */
export const CAP = {
  quoteCreate: 'quote.create',
  quoteSend: 'quote.send',

  caseCreate: 'case.create',
  caseCloseOperational: 'case.close.operational',
  caseCloseFinancial: 'case.close.financial',
  caseRouteEdit: 'case.route.edit',
  caseTeamAssign: 'case.team.assign',
  caseReopen: 'case.reopen',

  directoryClientCreate: 'directory.client.create',
  directoryClientManage: 'directory.client.manage',
  directoryParticipantCreate: 'directory.participant.create',
  directoryParticipantAgentCreate: 'directory.participant.agent.create',
  directoryParticipantManage: 'directory.participant.manage',
  directoryTransportManage: 'directory.transport.manage',

  financeExpenseRecord: 'finance.expense.record',
  financePaymentRecord: 'finance.payment.record',
  /* The outbound act on a leg — recognise the carrier's debt, record the
     payment and allocate it, in one request (0059, spec L7). Deliberately NOT
     a widening of `financePaymentRecord`: Operations gets the one payment their
     work needs and nothing else — not the inbound side, not the cross-case
     register. */
  financePaymentCarrier: 'finance.payment.carrier',
  /* The second pair of eyes: confirm a recorded payment, or delete a pending
     one with a reason. Finance's, and the only thing that reveals either
     control — hiding them is a courtesy, and the server re-checks on the
     request (AD-21). */
  financePaymentConfirm: 'finance.payment.confirm',
  financeReceivablesRead: 'finance.receivables.read',

  documentInvoiceIssue: 'document.invoice.issue',
  documentOfferRender: 'document.offer.render',

  adminUsersRead: 'admin.users.read',
  adminUsersManage: 'admin.users.manage',
  adminRolesRead: 'admin.roles.read',
  adminRolesManage: 'admin.roles.manage',
  adminSettingsManage: 'admin.settings.manage',
} as const satisfies Record<string, Capability>
